import React, { FC, useMemo } from "react";

import { getKeys } from "../../../utils/object";
import { Chip } from "../../forms";
import { TagColors } from "../../forms/elements/tags/types";
import { materialsMapReverse } from "../materials-map";
import { EventsFiltersForm, EventsLabels } from "../types";

export interface EventsFiltersSummaryProps {
  labels: EventsLabels;
  appliedFilters: EventsFiltersForm;
  setAppliedFilters: (filters: EventsFiltersForm) => void;
  onReset: () => void;
}

export const EventsFiltersSummary: FC<EventsFiltersSummaryProps> = ({
  labels,
  appliedFilters,
  setAppliedFilters,
  onReset,
}) => {
  const { country, eventType, materials, search } = appliedFilters;

  const hasFilters = useMemo(() => {
    return getKeys(appliedFilters).reduce((acc, key) => {
      if (key === "timeInterval") return acc;

      const filter = appliedFilters[key];

      if (typeof filter === "object") {
        const hasInnerFilter = getKeys(filter).reduce(
          (acc, innerKey) => acc || !!filter[innerKey],
          false
        );
        return acc || hasInnerFilter;
      }

      return acc || !!appliedFilters[key];
    }, false);
  }, [appliedFilters]);

  return (
    <div className="flex mt-4">
      <div className="flex flex-wrap gap-4">
        {search && (
          <Chip
            color={TagColors.Neutral}
            onChange={() =>
              setAppliedFilters({
                ...appliedFilters,
                search: "",
              })
            }
          >
            <strong>{labels["events-filters-search"]}:</strong> {search}
          </Chip>
        )}
        {country && (
          <Chip
            color={TagColors.Neutral}
            onChange={() =>
              setAppliedFilters({
                ...appliedFilters,
                country: "",
              })
            }
          >
            <strong>{labels["events-filters-country"]}:</strong> {country}
          </Chip>
        )}
        {eventType["IN-PERSON"] && (
          <Chip
            color={TagColors.Neutral}
            onChange={() =>
              setAppliedFilters({
                ...appliedFilters,
                eventType: {
                  ...appliedFilters.eventType,
                  "IN-PERSON": false,
                },
              })
            }
          >
            {labels["event-type-in-presence"]}
          </Chip>
        )}
        {eventType.DIGITAL && (
          <Chip
            color={TagColors.Neutral}
            onChange={() =>
              setAppliedFilters({
                ...appliedFilters,
                eventType: {
                  ...appliedFilters.eventType,
                  DIGITAL: false,
                },
              })
            }
          >
            {labels["event-type-online"]}
          </Chip>
        )}
        {getKeys(materials)
          .filter((materialKey) => materials[materialKey])
          .map((materialKey) => (
            <Chip
              color={materialsMapReverse[materialKey].tagColor}
              key={materialKey}
              onChange={() =>
                setAppliedFilters({
                  ...appliedFilters,
                  materials: {
                    ...appliedFilters.materials,
                    [materialKey]: false,
                  },
                })
              }
            >
              {labels[materialKey]}
            </Chip>
          ))}
      </div>
      {hasFilters && (
        <button
          type="button"
          className="font-bold text-primary hover:underline p-2 flex-none ml-auto"
          onClick={onReset}
        >
          {labels["events-filters-clear-button"]}
        </button>
      )}
    </div>
  );
};
